// src/pages/worksheets/TemplateTaskCreator.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../firebase";
import TaskCreator, { FileItem } from "../../taskcreator/TaskCreator";
import {
    FileText,
    ArrowLeft,
    LayoutTemplate,
    Sparkles,
    Brain,
    Settings,
    AlertCircle,
    Check,
    Info,
    Zap,
    Target,
    Clock,
    BarChart,
    RefreshCw,
} from "lucide-react";

export default function TemplateTaskCreator() {
    const { templateId } = useParams<{ templateId: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [template, setTemplate] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [analysis, setAnalysis] = useState<any>(null);
    const [settings, setSettings] = useState({
        preserveStructure: true,
        matchStyle: true,
        autoAdjustTasks: true,
    });

    // Lade Template und Analyse
    useEffect(() => {
        if (!templateId || !user) return;

        const loadTemplateAndAnalysis = async () => {
            try {
                // Lade Template
                const getTemplates = httpsCallable(getFunctions(app), "getUserTemplates");
                const result: any = await getTemplates({ userId: user.uid });

                if (result.data?.templates) {
                    const foundTemplate = result.data.templates.find((t: any) => t.id === templateId);
                    if (foundTemplate) {
                        setTemplate(foundTemplate);

                        // Lade Analyse
                        const analyzeFn = httpsCallable(getFunctions(app), "analyzeTemplate");
                        const analysisResult: any = await analyzeFn({
                            templateId,
                            userId: user.uid,
                        });

                        if (analysisResult.data?.analysis) {
                            setAnalysis(analysisResult.data.analysis);
                        }
                    } else {
                        setError("Vorlage nicht gefunden");
                    }
                } else if (result.data?.error) {
                    setError(result.data.error);
                }
            } catch (err: any) {
                console.error("Fehler beim Laden:", err);
                setError("Vorlage konnte nicht geladen werden");
            } finally {
                setLoading(false);
            }
        };

        loadTemplateAndAnalysis();
    }, [templateId, user]);

    // Erweiterte Generate-Funktion
    const generateWithTemplate = async (
        topic: string,
        context: string,
        difficulty: string,
        materialText: string,
        numSingle: number,
        numMultiple: number,
        numFree: number,
        numGap: number,
        taskFiles?: FileItem[],
        contentSource?: "manual" | "youtube",
        youtubeUrl?: string
    ) => {
        if (!user || !template) return { error: "Vorlage nicht verfügbar" };

        try {
            const generateFunction = httpsCallable(getFunctions(app), "generateTasksWithTemplate");

            // Passe Aufgabenanzahl basierend auf Analyse an
            let adjustedTasks = {
                numSingle,
                numMultiple,
                numFree,
                numGap,
            };

            if (settings.autoAdjustTasks && analysis) {
                if (analysis.recommendedTaskTypes) {
                    adjustedTasks = {
                        numSingle: analysis.recommendedTaskTypes.includes('singleChoice') ? numSingle : 0,
                        numMultiple: analysis.recommendedTaskTypes.includes('multipleChoice') ? numMultiple : 0,
                        numFree: analysis.recommendedTaskTypes.includes('freeText') ? numFree : 0,
                        numGap: analysis.recommendedTaskTypes.includes('gapText') ? numGap : 0,
                    };
                }
            }

            const result: any = await generateFunction({
                templateId: template.id,
                userId: user.uid,
                topic,
                difficulty: analysis?.difficultyMatch || difficulty,
                context,
                materialText,
                ...adjustedTasks,
                preserveTemplateStructure: settings.preserveStructure,
                matchTemplateStyle: settings.matchStyle,
            });

            if (result.data?.file) {
                const blob = b64toBlob(
                    result.data.file,
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Arbeitsblatt_${topic.replace(/[^a-z0-9]/gi, '_')}.docx`;
                a.click();
                URL.revokeObjectURL(url);

                return {
                    success: true,
                    analysis: result.data.analysis
                };
            } else if (result.data?.error) {
                return { error: result.data.error };
            }
        } catch (err: any) {
            console.error("Fehler bei der Vorlagengenerierung:", err);
            return { error: `Generierung fehlgeschlagen: ${err.message}` };
        }

        return { error: "Unbekannter Fehler" };
    };

    const b64toBlob = (base64: string, type: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-gray-700 border-t-green-500 rounded-full animate-spin mx-auto mb-6" />
                    <div className="text-white text-xl font-semibold">Analysiere Vorlage...</div>
                    <p className="text-gray-400 mt-2">Die KI analysiert die Vorlagenstruktur</p>
                </div>
            </div>
        );
    }

    if (error || !template) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 flex items-center justify-center p-4">
                <div className="bg-gray-900/80 border border-gray-800 rounded-2xl p-8 max-w-md w-full">
                    <div className="flex items-center gap-3 text-red-500 mb-4">
                        <AlertCircle size={24} />
                        <h2 className="text-xl font-bold">Fehler</h2>
                    </div>
                    <p className="text-gray-300 mb-6">{error || "Vorlage nicht gefunden"}</p>
                    <button
                        onClick={() => navigate("/worksheets")}
                        className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
                    >
                        <ArrowLeft size={18} />
                        Zurück zu Vorlagen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border-b border-green-500/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate("/worksheets")}
                                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                            >
                                <ArrowLeft size={20} />
                                Zurück
                            </button>

                            <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 w-12 h-12 rounded-xl flex items-center justify-center">
                                <LayoutTemplate size={24} className="text-green-500" />
                            </div>

                            <div>
                                <h1 className="text-2xl font-bold text-white">
                                    Intelligente Vorlage: {template.fileName}
                                </h1>
                                <p className="text-gray-400">
                                    Die KI erkennt automatisch die Struktur
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => {
                                setSettings({
                                    preserveStructure: true,
                                    matchStyle: true,
                                    autoAdjustTasks: true,
                                });
                            }}
                            className="bg-gray-800/50 border border-gray-700 text-gray-300 px-4 py-2 rounded-xl font-semibold hover:border-green-500 hover:text-green-500 transition-all flex items-center gap-2"
                        >
                            <RefreshCw size={18} />
                            Einstellungen zurücksetzen
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Linke Spalte: Analyse und Einstellungen */}
                <div className="lg:col-span-1 space-y-6">
                    {/* KI-Analyse */}
                    {analysis && (
                        <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Brain size={24} className="text-green-500" />
                                <h3 className="text-xl font-bold text-white">KI-Analyse</h3>
                            </div>

                            <div className="space-y-4">
                                <div className="p-4 bg-gray-800/30 rounded-xl">
                                    <div className="text-sm text-gray-400 mb-1">Zusammenfassung</div>
                                    <p className="text-white">{analysis.summary}</p>
                                </div>

                                <div>
                                    <div className="text-sm text-gray-400 mb-2">Empfohlene Aufgabentypen</div>
                                    <div className="flex flex-wrap gap-2">
                                        {analysis.recommendedTaskTypes?.map((type: string, index: number) => (
                                            <span key={index} className="inline-flex items-center gap-1 bg-green-500/20 text-green-500 text-xs px-3 py-1 rounded-full">
                                                <Check size={10} />
                                                {type === 'singleChoice' && 'Single Choice'}
                                                {type === 'multipleChoice' && 'Multiple Choice'}
                                                {type === 'freeText' && 'Freitext'}
                                                {type === 'gapText' && 'Lückentext'}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-3 bg-gray-800/30 rounded-lg">
                                        <div className="text-sm text-gray-400">Schwierigkeit</div>
                                        <div className="text-white font-semibold">{analysis.difficultyMatch}</div>
                                    </div>
                                    <div className="p-3 bg-gray-800/30 rounded-lg">
                                        <div className="text-sm text-gray-400">Dauer</div>
                                        <div className="text-white font-semibold">{analysis.estimatedTime} min</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Vorlagen-Einstellungen */}
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <Settings size={24} className="text-blue-500" />
                            <h3 className="text-xl font-bold text-white">Integrations-Einstellungen</h3>
                        </div>

                        <div className="space-y-4">
                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                                        <Sparkles size={18} className="text-green-500" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-white">Intelligente Integration</div>
                                        <div className="text-sm text-gray-400">Erkennt automatisch Struktur</div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={settings.preserveStructure}
                                    onChange={(e) => setSettings({...settings, preserveStructure: e.target.checked})}
                                    className="relative w-12 h-6 rounded-full bg-gray-700 checked:bg-green-500 transition-colors"
                                />
                            </label>

                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                        <Target size={18} className="text-blue-500" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-white">Stil anpassen</div>
                                        <div className="text-sm text-gray-400">Übernimmt Vorlagen-Design</div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={settings.matchStyle}
                                    onChange={(e) => setSettings({...settings, matchStyle: e.target.checked})}
                                    className="relative w-12 h-6 rounded-full bg-gray-700 checked:bg-green-500 transition-colors"
                                />
                            </label>

                            <label className="flex items-center justify-between cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                        <Zap size={18} className="text-purple-500" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-white">Aufgaben automatisch anpassen</div>
                                        <div className="text-sm text-gray-400">Basierend auf KI-Analyse</div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={settings.autoAdjustTasks}
                                    onChange={(e) => setSettings({...settings, autoAdjustTasks: e.target.checked})}
                                    className="relative w-12 h-6 rounded-full bg-gray-700 checked:bg-green-500 transition-colors"
                                />
                            </label>
                        </div>

                        <div className="mt-6 p-4 bg-gray-800/30 rounded-xl">
                            <div className="flex items-center gap-2 text-green-500 mb-2">
                                <Info size={16} />
                                <span className="font-semibold">Hinweis</span>
                            </div>
                            <p className="text-sm text-gray-300">
                                Die KI analysiert Ihre Vorlage und passt die generierten Aufgaben automatisch an Struktur und Stil an.
                                Sie können beliebige Vorlagen verwenden - auch alte Arbeitsblätter oder Tests.
                            </p>
                        </div>
                    </div>

                    {/* Vorlagen-Info */}
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4">📋 Erkannte Vorlagen-Struktur</h4>
                        <div className="space-y-3">
                            {template.templateInfo.structure.sections?.slice(0, 5).map((section: any, index: number) => (
                                <div key={index} className="p-3 bg-gray-800/30 rounded-lg">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-sm font-semibold text-gray-300">
                                            {section.type === 'question' && '❓ Frage'}
                                            {section.type === 'answer' && '📝 Antwort'}
                                            {section.type === 'exercise' && '💪 Übung'}
                                            {section.type === 'instruction' && '📋 Anleitung'}
                                            {section.type === 'title' && '🏷️ Titel'}
                                            {section.type === 'content' && '📄 Inhalt'}
                                            {section.type === 'unknown' && '❔ Unbekannt'}
                                        </span>
                                        <span className="text-xs text-gray-500">Abschnitt {index + 1}</span>
                                    </div>
                                    <p className="text-sm text-gray-400 truncate">{section.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Rechte Spalte: Task Creator */}
                <div className="lg:col-span-2">
                    <TaskCreator
                        defaultTopic={template.templateInfo.structure.sections?.[0]?.suggestedContent || ""}
                        templateMode={true}
                        templateName={template.fileName}
                        onGenerateWithTemplate={generateWithTemplate}
                    />
                </div>
            </div>
        </div>
    );
}